import { useCallback, useEffect, useState } from "react";
import { Users, UserPlus, X, Loader2, Pencil, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const PERM_LABELS = { add: "Ekleme", edit: "Düzenleme", delete: "Silme" };

function PermChecks({ value, onChange, disabled, idPrefix }) {
  return (
    <div className="flex items-center gap-4">
      {Object.keys(PERM_LABELS).map((p) => (
        <label key={p} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <Checkbox data-testid={`${idPrefix}-perm-${p}`} checked={!!value[p]} disabled={disabled}
            onCheckedChange={(c) => onChange({ ...value, [p]: !!c })} />
          {PERM_LABELS[p]}
        </label>
      ))}
    </div>
  );
}

export default function AccountManager() {
  const { user, setUser } = useAuth();
  const isOwner = user?.role === "owner";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [name, setName] = useState(user?.account?.name || "");
  const [editingName, setEditingName] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePerms, setInvitePerms] = useState({ add: true, edit: false, delete: false });
  const [busy, setBusy] = useState(false);
  const [newAccName, setNewAccName] = useState("");
  const [creating, setCreating] = useState(false);

  const createAccount = async () => {
    if (!newAccName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/accounts", { name: newAccName.trim() });
      toast.success(`"${newAccName.trim()}" hesabı oluşturuldu, geçiş yapıldı`);
      setUser(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setCreating(false);
    }
  };

  const load = useCallback(async () => {
    const m = await api.get("/accounts/members");
    setMembers(m.data);
    if (isOwner) {
      const i = await api.get("/accounts/invites");
      setInvites(i.data);
    }
  }, [isOwner]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    try {
      await api.put("/accounts/current", { name: name.trim() });
      setUser({ ...user, account: { ...user.account, name: name.trim() } });
      setEditingName(false);
      toast.success("Hesap adı güncellendi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      await api.post("/accounts/invite", { email: inviteEmail.trim(), permissions: invitePerms });
      toast.success("Davet gönderildi. Davet edilen kişi bu e-posta ile giriş yaptığında daveti görecek.");
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const updatePerms = async (m, perms) => {
    setMembers(members.map((x) => (x.user_id === m.user_id ? { ...x, permissions: perms } : x)));
    try {
      await api.put(`/accounts/members/${m.user_id}`, perms);
      toast.success(`${m.name || m.email} yetkileri güncellendi`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/accounts/members/${m.user_id}`);
      toast.success(`${m.name || m.email} hesaptan çıkarıldı`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const cancelInvite = async (inv) => {
    try {
      await api.delete(`/accounts/invites/${inv.id}`);
      load();
    } catch {
      toast.error("Davet iptal edilemedi");
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-6" data-testid="account-manager">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-semibold">Hesap Yönetimi</h2>
          <p className="text-xs text-muted-foreground">
            {isOwner ? "Üyeleri davet edin ve yetkilerini belirleyin" : "Bu hesapta üyesiniz — yetkilerinizi hesap sahibi belirler"}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Hesap Adı</p>
        {editingName ? (
          <div className="flex gap-2 max-w-sm">
            <Input data-testid="account-name-input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
            <Button data-testid="account-name-save" variant="outline" onClick={saveName} disabled={!name.trim()}
              className="rounded-full border-border shrink-0"><Check className="w-4 h-4" /></Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span data-testid="account-name-display" className="text-sm text-primary font-semibold">{user?.account?.name}</span>
            {isOwner && (
              <button data-testid="account-name-edit" onClick={() => { setName(user?.account?.name || ""); setEditingName(true); }}
                className="p-1 text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">Üyeler ({members.length})</p>
        <div className="space-y-3" data-testid="members-list">
          {members.map((m) => (
            <div key={m.user_id} data-testid={`member-${m.email}`}
              className="flex flex-col sm:flex-row sm:items-center gap-2 bg-secondary/30 border border-border rounded-xl px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {m.name || m.email}
                  {m.role === "owner" && <span className="ml-2 text-xs bg-primary/15 text-primary rounded-full px-2 py-0.5">Hesap Sahibi</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              {m.role !== "owner" && (
                <div className="flex items-center gap-3">
                  <PermChecks value={m.permissions} disabled={!isOwner} idPrefix={`member-${m.email}`}
                    onChange={(p) => updatePerms(m, p)} />
                  {isOwner && (
                    <button data-testid={`remove-member-${m.email}`} onClick={() => removeMember(m)} title="Üyeyi çıkar"
                      className="p-1.5 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isOwner && (
        <div className="space-y-3 pt-2 border-t border-border">
          <p className="text-sm font-medium pt-3 flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" /> Yeni Üye Davet Et</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input data-testid="invite-email-input" type="email" placeholder="uye@ornek.com" className="max-w-xs"
              value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            <PermChecks value={invitePerms} onChange={setInvitePerms} idPrefix="invite" />
            <Button data-testid="invite-send-button" onClick={sendInvite} disabled={busy || !inviteEmail.trim()}
              className="rounded-full font-semibold shrink-0">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />} Davet Et
            </Button>
          </div>
          {invites.length > 0 && (
            <div className="space-y-2" data-testid="pending-invites-list">
              <p className="text-xs text-muted-foreground">Bekleyen davetler:</p>
              {invites.map((inv) => (
                <div key={inv.id} className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{inv.email}</span>
                  <button data-testid={`cancel-invite-${inv.email}`} onClick={() => cancelInvite(inv)}
                    className="text-xs text-red-400 hover:underline">İptal</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3 pt-2 border-t border-border">
        <p className="text-sm font-medium pt-3 flex items-center gap-2"><Plus className="w-4 h-4 text-primary" /> Yeni Hesap Oluştur</p>
        <p className="text-xs text-muted-foreground">Örn: "Ev Bütçesi", "İş Yeri" — her hesabın verileri ayrı tutulur, sahibi siz olursunuz</p>
        <div className="flex gap-2">
          <Input data-testid="new-account-name-input" placeholder="Hesap adı" maxLength={60} className="max-w-xs"
            value={newAccName} onChange={(e) => setNewAccName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), createAccount())} />
          <Button data-testid="new-account-create-button" variant="outline" onClick={createAccount}
            disabled={creating || !newAccName.trim()} className="rounded-full border-border hover:bg-secondary shrink-0">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Oluştur
          </Button>
        </div>
      </div>
    </div>
  );
}
